import useMiyoFee from '@/hooks/useMiyoFee';
import { colorAttributeCompare, defaultAttributeCompare } from '@features/pdp/attribute-sorting';
import { getPDPV2Data } from '@features/pdp/pdp-v2.data';
import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/store';
import { getItem } from '../../../../actions/pdp.actions';
import AccessoryPickers from '../../../../components/AccessoryPickers';
import ColorSwatches from '../../../../components/ColorSwatches';
import PartnerDesignGallery from '../PartnerDesignGallery';
import PersonalizeProduct from '../PersonalizeProduct';
import Swatches from './Swatches';
import AccessoryUpsell from '@components/tailwind/AccessoryUpsell/AccessoryUpsell';
import { abtastyService } from '@/services';

type Props = {
  className?: string;
  showAccessoryPickers: boolean;
  setAccessoryPickerValues?: (ref) => void;
  onlyShow?: string[];
  swatchSize?: 'sm' | 'md';
  dimensionsScroll?: React.ReactNode;
  onCustomize?: (e: any) => void;
  hideOOS?: boolean;
  addendum?: React.ReactNode;
};

const swatchesOrder = {
  default: {
    gender: 1,
    name: 2,
    size: 3,
    color: 4,
    // Partner Design Gallery is 5
    // Personalize with MyRTIC is 6
    // Bundles is 7
    // Accessory Upsell is 8
    material: 9,
    additions: 10,
    width: 11,
    uom: 12,
  },
  cuero: {
    color: 1,
    size: 2,
    name: 3,
    width: 4,
    gender: 5,
    material: 9,
    additions: 10,
    uom: 11,
  },
};

const ProductSwatches = ({
  className = '',
  showAccessoryPickers,
  setAccessoryPickerValues = () => {},
  onlyShow,
  onCustomize,
  swatchSize = 'md',
  dimensionsScroll,
  hideOOS = false,
  addendum,
}: Props) => {
  const dispatch = useAppDispatch();
  const { variants, site, details, miyo } = useSelector((state: any) => state.pdpItem);
  const isPartnerView = !!miyo?.partnerConfig?.partner;

  const typeId = details?.attributes?.typeId;
  const extra = useMemo(() => getPDPV2Data(typeId)?.variants, [typeId]);

  const miyoFee = useMiyoFee();

  const accessoryPickerRef = useCallback((newRef) => newRef && setAccessoryPickerValues(newRef), []);

  const isRtic = site?.id === 2;
  const isCuero = site?.id === 4;
  const selectedSwatchesOrder = useMemo(
    () => (isCuero ? swatchesOrder.cuero : swatchesOrder.default),
    [site]
  );

  const changeItemHandler = (swatchData) => async () => {
    dispatch(getItem(swatchData.url));

    const swatchLabelCapitalized = swatchData.key.charAt(0).toUpperCase() + swatchData.key.slice(1);
    abtastyService.trackClick(`PDP: ${swatchLabelCapitalized} Swatch`);
  };

  const showVariants = useMemo(() => {
    const values = {
      genders: variants?.show?.genders,
      names: variants?.show?.names,
      sizes: variants?.show?.sizes,
      colors: variants?.show?.colors,
      materials: variants?.show?.materials,
      widths: variants?.show?.widths,
      units: variants?.show?.units,
    };

    if (!!onlyShow && onlyShow.length > 0) {
      return {
        genders: onlyShow.includes('genders') && variants?.show?.genders,
        names: onlyShow.includes('names') && variants?.show?.names,
        sizes: onlyShow.includes('sizes') && variants?.show?.sizes,
        colors: onlyShow.includes('colors') && variants?.show?.colors,
        materials: onlyShow.includes('materials') && variants?.show?.materials,
        widths: onlyShow.includes('widths') && variants?.show?.widths,
        units: onlyShow.includes('units') && variants?.show?.units,
      };
    }

    return values;
  }, [onlyShow, variants]);

  if (!variants) return null;

  return (
    <div className={classNames('tw:flex tw:flex-col tw:gap-3', className)}>
      {showVariants.genders && (
        <Swatches
          label='Gender'
          attributes={variants?.genders?.toSorted(defaultAttributeCompare)}
          activeAttribute={variants?.activeSex}
          itemHandler={changeItemHandler}
          wrapperClassName={`tw:w-full tw:mt-1 tw:mb-3 tw:order-${selectedSwatchesOrder.gender}`}
          indicateSale={isRtic && variants?.flagGenderSales}
          swatchSize={swatchSize}
          extra={extra}
          hideOOS={hideOOS}
        />
      )}
      {showVariants.names && (
        <Swatches
          label='Name'
          attributes={variants?.names?.toSorted(defaultAttributeCompare)}
          activeAttribute={variants?.activeName}
          itemHandler={changeItemHandler}
          wrapperClassName={`tw:w-full tw:mt-1 tw:mb-3 tw:order-${selectedSwatchesOrder.name}`}
          indicateSale={isRtic && variants?.flagNameSales}
          swatchSize={swatchSize}
          extra={extra}
          hideOOS={hideOOS}
        />
      )}
      {showVariants.sizes && (
        <>
          {dimensionsScroll}
          <Swatches
            label='Size'
            attributes={variants?.sizes?.toSorted(defaultAttributeCompare)}
            activeAttribute={variants?.activeSize}
            itemHandler={changeItemHandler}
            wrapperClassName={`tw:w-full tw:mt-1 tw:mb-3 tw:order-${selectedSwatchesOrder.size}`}
            indicateSale={isRtic && variants?.flagSizeSales}
            swatchSize={swatchSize}
            extra={extra}
            hideOOS={hideOOS}
          />
        </>
      )}

      {showVariants.colors && (
        <ColorSwatches
          hideOOS={hideOOS}
          colors={variants?.colors?.toSorted(colorAttributeCompare)}
          activeColor={variants?.activeColor}
          itemHandler={changeItemHandler}
          wrapperClassName={`tw:w-full tw:mt-1 tw:mb-3 tw:order-${selectedSwatchesOrder.color}`}
          indicateSale={isRtic && variants?.flagColorSales}
          swatchSize={swatchSize}
          feeTotal={miyoFee}
        />
      )}

      <PartnerDesignGallery className='tw:order-5' />

      <PersonalizeProduct onCustomize={onCustomize} className='tw:order-6' />

      {!isPartnerView && <AccessoryUpsell className='tw:order-8' />}

      {showVariants.materials && variants?.materials && variants.materials.length > 1 && (
        <Swatches
          label='Material'
          attributes={variants?.materials?.toSorted(defaultAttributeCompare)}
          activeAttribute={variants?.activeMaterial}
          itemHandler={changeItemHandler}
          wrapperClassName={`tw:w-full tw:mt-1 tw:mb-3 tw:order-${selectedSwatchesOrder.material}`}
          indicateSale={isRtic && variants?.flagMaterialSales}
          swatchSize={swatchSize}
          extra={extra}
          hideOOS={hideOOS}
        />
      )}

      {showAccessoryPickers && (
        <AccessoryPickers
          ref={accessoryPickerRef}
          className={`tw:order-${selectedSwatchesOrder.additions}`}
        />
      )}

      {showVariants.widths && (
        <Swatches
          label='Width'
          attributes={variants?.widths?.toSorted(defaultAttributeCompare)}
          activeAttribute={variants?.activeWidth}
          itemHandler={changeItemHandler}
          wrapperClassName={`tw:w-full tw:mt-1 tw:mb-3 tw:order-${selectedSwatchesOrder.width}`}
          indicateSale={isRtic && variants?.flagWidthSales}
          swatchSize={swatchSize}
          extra={extra}
          hideOOS={hideOOS}
        />
      )}
      {showVariants.units && (
        <Swatches
          label='Unit'
          attributes={variants?.units?.toSorted(defaultAttributeCompare)}
          activeAttribute={variants?.activeUom}
          itemHandler={changeItemHandler}
          wrapperClassName={`tw:w-full tw:mt-1 tw:mb-3 tw:order-${selectedSwatchesOrder.uom}`}
          indicateSale={isRtic && variants?.flagUnitSales}
          swatchSize={swatchSize}
          extra={extra}
          hideOOS={hideOOS}
        />
      )}

      {addendum}
    </div>
  );
};

export default ProductSwatches;
